import {
  AggregationGroupBy,
  WorkpackTypeFilterOption,
  WbsFilterOption,
  useGetUserPreferencesQuery,
} from '@/__generated__/graphql';
import { useRouter } from '@/hooks';
import { useEffect, useMemo } from 'react';
import { kpiFilterSchema } from '../schemas';
import { useSetAtom } from 'jotai';
import * as kpiState from '../state';
import { RESET } from 'jotai/utils';

/**
 * Intersects `saved` with `validStructureIds`. If there are no valid structures, returns `[]`.
 * If nothing is saved, returns all valid IDs. If some saved IDs are no longer valid and none
 * remain after intersecting, returns all valid IDs (stale preferences).
 *
 * `validStructureIds` may be nullish (treated like `[]`).
 */
export const normalizeKpiSelectedDeploymentIds = (
  saved: string[] | undefined,
  validStructureIds: string[] | null | undefined
): string[] => {
  const validIds = validStructureIds ?? [];
  if (validIds.length === 0) {
    return [];
  }
  if (!saved?.length) {
    return validIds;
  }
  const valid = new Set(validIds);
  const intersected = saved.filter((id) => valid.has(id));
  return intersected.length > 0 ? intersected : validIds;
};

export const useInitializeKpiFiltersEffect = (validKpiStructureIds?: string[] | null) => {
  const router = useRouter();
  const { platformId } = router.query;
  const { data, loading, error } = useGetUserPreferencesQuery({
    skip: typeof platformId !== 'string',
  });

  const setSelectedDeploymentIds = useSetAtom(kpiState.selectedDeploymentIds);
  const setSelectedDateAggregation = useSetAtom(kpiState.selectedDateAggregation);
  const setSelectedStartDate = useSetAtom(kpiState.selectedStartDate);
  const setSelectedEndDate = useSetAtom(kpiState.selectedEndDate);
  const setVisibleColumnKeys = useSetAtom(kpiState.visibleColumnKeys);
  const setKpiExportColumnKeys = useSetAtom(kpiState.kpiExportColumnKeys);
  const setSelectedWorkpackTypes = useSetAtom(kpiState.selectedWorkpackTypes);
  const setSelectedWbsFilter = useSetAtom(kpiState.selectedWbsFilter);

  const currentPlatformPreferences = useMemo(() => {
    if (loading) return;
    if (error) {
      console.error(error);
      return;
    }
    if (!data?.getUserPreferences?.platform) return;
    if (typeof platformId !== 'string') return;

    const preferences = kpiFilterSchema.safeParse(data.getUserPreferences.platform?.[platformId]);
    if (!preferences.success) {
      console.error(preferences.error);
      return;
    }

    return preferences.data;
  }, [data?.getUserPreferences?.platform, error, loading, platformId]);

  useEffect(() => {
    if (loading || typeof platformId !== 'string') {
      return;
    }
    const saved = currentPlatformPreferences?.filters?.kpi_selected_deployments;
    setSelectedDeploymentIds(normalizeKpiSelectedDeploymentIds(saved, validKpiStructureIds));
  }, [
    currentPlatformPreferences,
    currentPlatformPreferences?.filters?.kpi_selected_deployments,
    loading,
    platformId,
    setSelectedDeploymentIds,
    validKpiStructureIds,
  ]);

  useEffect(() => {
    const filters = currentPlatformPreferences?.filters;
    const columns = currentPlatformPreferences?.columns;

    if (filters?.kpi_aggregation_filter) {
      setSelectedDateAggregation(filters.kpi_aggregation_filter as AggregationGroupBy);
    }
    if (filters?.kpi_date_range) {
      const { from, to } = filters.kpi_date_range;

      if (from !== null && from !== undefined) {
        const startDate = new Date(from);
        if (!Number.isNaN(startDate.getTime())) {
          setSelectedStartDate(startDate);
        } else {
          setSelectedStartDate(undefined);
        }
      } else {
        setSelectedStartDate(undefined);
      }

      if (to !== null && to !== undefined) {
        const endDate = new Date(to);
        if (!Number.isNaN(endDate.getTime())) {
          setSelectedEndDate(endDate);
        } else {
          setSelectedEndDate(undefined);
        }
      } else {
        setSelectedEndDate(undefined);
      }
    } else {
      // If no date range filter exists, clear both dates
      setSelectedStartDate(undefined);
      setSelectedEndDate(undefined);
    }
    if (filters?.kpi_selected_workpack_types) {
      setSelectedWorkpackTypes(filters.kpi_selected_workpack_types as WorkpackTypeFilterOption[]);
    }

    if (columns?.kpi_table) {
      setVisibleColumnKeys(columns.kpi_table);
    }

    if (columns?.kpi_export) {
      setKpiExportColumnKeys(columns.kpi_export);
    }

    if (filters?.kpi_selected_wbs) {
      setSelectedWbsFilter(filters.kpi_selected_wbs as WbsFilterOption);
    }
  }, [
    currentPlatformPreferences?.filters,
    setSelectedDateAggregation,
    setSelectedStartDate,
    setSelectedEndDate,
    setVisibleColumnKeys,
    setKpiExportColumnKeys,
    setSelectedWorkpackTypes,
    currentPlatformPreferences?.columns,
    setSelectedWbsFilter,
  ]);

  useEffect(() => {
    return () => {
      setSelectedDeploymentIds(RESET);
      setSelectedDateAggregation(RESET);
      setSelectedStartDate(RESET);
      setSelectedEndDate(RESET);
      setVisibleColumnKeys(RESET);
      setKpiExportColumnKeys(RESET);
      setSelectedWorkpackTypes(RESET);
      setSelectedWbsFilter(RESET);
    };
  }, [
    setSelectedDeploymentIds,
    setSelectedDateAggregation,
    setSelectedStartDate,
    setSelectedEndDate,
    setVisibleColumnKeys,
    setKpiExportColumnKeys,
    setSelectedWorkpackTypes,
    setSelectedWbsFilter,
  ]);
};
